/**
 * Turns a failed `askAssistant` call into a message worth showing in the chat.
 *
 * The panel previously rendered "Sorry, I couldn't connect" for every failure,
 * so a server-side fault was indistinguishable from a dropped network. That is
 * what hid a retired Gemini model behind a connection message. Each branch below
 * tells the user something different and actionable; only a genuine transport
 * failure is allowed to blame the connection.
 *
 * Raw error text never reaches the screen — the detail belongs in the function
 * logs, not the UI. Mirrors `friendlyError` in the LCA app.
 */

/** firebase/functions throws a FunctionsError: an Error carrying a `code`. */
function codeOf(err: unknown): string {
  return typeof err === "object" && err !== null && "code" in err
    ? String((err as { code: unknown }).code)
    : "";
}

export function assistantErrorMessage(err: unknown): string {
  switch (codeOf(err)) {
    case "functions/unauthenticated":
      return "Your session expired. Reload the page and sign in again.";

    case "functions/permission-denied":
      return "Your account doesn't have access to the assistant. Ask an admin to check your profile.";

    case "functions/resource-exhausted":
      return "The assistant is busy right now. Try again in a minute.";

    case "functions/deadline-exceeded":
      return "That took too long to answer. Try asking again.";

    case "functions/unavailable":
      return "The assistant is temporarily unavailable. Try again in a moment.";

    // The function reached us and failed on its own side — a real server fault,
    // not a connection problem. Saying otherwise sends people to debug Wi-Fi.
    case "functions/internal":
      return "The assistant hit an error on our end. It's been logged — please try again.";
  }

  const message = err instanceof Error ? err.message : String(err);
  if (/failed to fetch|networkerror|offline|network/i.test(message)) {
    return "Couldn't reach the assistant. Check your connection and try again.";
  }

  return "Something went wrong reaching the assistant. Please try again.";
}
